import { FilterOutlined } from "@ant-design/icons";
import { Button, Card, Col, DatePicker, message, Row, Select, Spin } from "antd";
import Graph from 'assets/Dashboard/graph.svg';
import { cardColorsShop } from "components/side-bar/utils";
import dayjs from "dayjs";
import { DashboardApiService } from "features/dashboard/api/dashboard-api.services";
import { BoothSalesperMonthView } from "features/dashboard/components/shop-graph/Booth-sales";
import { ItemSoldView } from "features/dashboard/components/shop-graph/Items-Sold";
import { PopularSoldView } from "features/dashboard/components/shop-graph/Popular-sold";
import { useGetCategoryShop } from "features/dashboard/use-cases/get-category-shop";
import { useGetDashboardCardCommon } from "features/dashboard/use-cases/get-commonDropDown";
import { useGetDashboardExctuiveSummaryData } from "features/dashboard/use-cases/get-exctuive-summary";
import { Fragment, useEffect, useState } from "react";

const { RangePicker } = DatePicker;

const years = Array.from({ length: 5 }, (_, i) => dayjs().year() - i).map((y) => ({ label: y, value: y }));

export const ShopCrossView = () => {
    const today = dayjs();

    // State
    const [cardData, setCardData] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [selectedYear, setSelectedYear] = useState<number>(today.year());
    const [selectedCategory, setSelectedCategory] = useState<string | undefined>();
    const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>('' as any);

    // API Params
    const commonParams = {
        FromDate: dateRange?.[0]?.format("YYYY-MM-DD") || "",
        ToDate: dateRange?.[1]?.format("YYYY-MM-DD") || "",
        Year: selectedYear,
        CategoryId: selectedCategory || "",
    };

    // Fetch dashboard card data
    const fetchCardData = async () => {
        try {
            setLoading(true);
            const response = await DashboardApiService().getDashboardshopSales(commonParams);
            setCardData(response || []);
        } catch (error: any) {
            message.error(error?.message || "Something went wrong!");
        } finally {
            setLoading(false);
        }
    };

    // Hooks for summary data
    const { data: summaryCategory, isFetching: isSummaryLoading } = useGetCategoryShop({
        ...commonParams,
        Flag: "TYPE_CATEGORY",
    } as any);

    const { data: popularData, isLoading: isPopularLoading } = useGetCategoryShop({
        ...commonParams,
        Flag: "POPULAR_ITEMS",
    } as any);

    const { data: shopSalesGraph, isLoading: isShopSalesLoading } = useGetDashboardExctuiveSummaryData({
        ...commonParams,
        Flag: "shopsales2",
    } as any);

    const { data: categoriesData } = useGetDashboardCardCommon({ Flag: "shopcategories" });

    // Initial fetch
    useEffect(() => {
        fetchCardData();
    }, [dateRange, selectedYear, selectedCategory]);

    // Handlers
    const handleApplyFilters = () => {
        fetchCardData();
    };

    const handleReset = () => {
        setSelectedYear(today.year());
        setSelectedCategory(undefined);
        setDateRange([today, today]);
        fetchCardData();
    };

    return (
        <Fragment>
            {/* Header */}
            <Col span={24}>
                <h2 style={{ fontSize: "16px", fontWeight: "500" }}>Shop</h2>
            </Col>

            {/* Filters */}
            <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                <Col span={24} style={{ padding: 0 }}>
                    <Card className="filtr_card filt_repn">
                        {/* Date Range */}
                        <RangePicker
                            style={{ marginRight: 16 }}
                            value={dateRange}
                            onChange={(dates) => {
                                setDateRange(dates as any);
                                setSelectedYear(today.year());
                            }}
                        />

                        {/* Year Filter */}
                        <Select
                            style={{ width: 120, marginRight: 16 }}
                            options={years}
                            value={selectedYear}
                            onChange={(value) => {
                                setSelectedYear(value);
                                setDateRange(null);
                            }}
                            placeholder="Select Year"
                        />

                        {/* Category Single-select */}
                        <Select
                            showSearch
                            optionFilterProp="label"
                            placeholder="Select Category"
                            value={selectedCategory}
                            onChange={setSelectedCategory}
                            style={{ width: 220, marginRight: 16 }}
                            dropdownRender={(menu) => (
                                <div>
                                    <div style={{ padding: 8, fontWeight: 500, borderBottom: "1px solid #f0f0f0" }}>
                                        <FilterOutlined style={{ marginRight: 6 }} /> Select Category
                                    </div>
                                    {menu}
                                </div>
                            )}
                            options={categoriesData?.dropDownList?.map((c: any) => ({
                                label: c.Name,
                                value: c.Id,
                            })) || []}
                        />

                        {/* Buttons */}
                        <Button type="primary" style={{ marginRight: 10 }} onClick={handleApplyFilters}>
                            Filter
                        </Button>
                        <Button onClick={handleReset}>Cancel</Button>
                    </Card>
                </Col>
            </Row>

            {/* Dashboard Cards */}
            <Row gutter={[16, 16]} className="row_gap respn_row">
                {cardData?.map((item: any, index: number) => (
                    <Col span={6} key={index}>
                        <Spin spinning={loading}>
                            <Card style={{ backgroundColor: cardColorsShop[item.Name] || "#fff" }}>
                                <p style={{ fontSize: 16, color: "#fff" }}>{item.Name}</p>
                                <div className="card_img_t">
                                    <h2 style={{ fontSize: 16, color: "#fff" }}>KWD {item.Value}</h2>
                                    <img src={Graph} alt="" width={40} height={40} />
                                </div>
                            </Card>
                        </Spin>
                    </Col>
                ))}
            </Row>

            {/* Graphs */}
            <Row gutter={[16, 16]} className="row_gap">
                <Col span={12} className="respn_col">
                    <ItemSoldView data={summaryCategory} loading={isSummaryLoading} />
                </Col>
                <Col span={12} className="respn_col">
                    <PopularSoldView data={popularData} loading={isPopularLoading} />
                </Col>
                <Col span={12} className="respn_col">
                    <BoothSalesperMonthView data={shopSalesGraph} loading={isShopSalesLoading} />
                </Col>
            </Row>
        </Fragment>
    );
};
